import React, { useEffect, useState, useRef } from 'react';
import { useTable, useGlobalFilter, useFilters, usePagination, useSortBy } from 'react-table';
import { GlobalFilter } from '../table/FilteringTable/GlobalFilter';
import { SidPagination } from './SidPagination';
import { listStateKey, readListState, writeListState } from './listState';
import { PageHeaderActions, usePageHeaderActionsSlot } from './pageHeaderActions';
import { Button, Modal } from "react-bootstrap";
import { Link } from 'react-router-dom';
import { Add, FilterSquare } from 'iconsax-react';
import './filtering.css';

import { useFiltriSullaRiga } from './useFiltriSullaRiga';
export const TabellaListaServer = (props) => {

	const [data, setDataJson] = useState([]);
	// Ricerca e filtri sulla stessa riga: prima si stringe la ricerca, poi i tasti (useFiltriSullaRiga)
	const rigaFiltri = useRef(null);
	useFiltriSullaRiga(rigaFiltri);
	const [columns, setColumns] = useState([]);
	const [message, setMessage] = useState('Caricamento in corso...');

	// Il testo cercato si ricorda per pagina e lista (vedi listState)
	const searchKey = listStateKey(props.cardTitle || '', 'ricerca');
	const [initialSearch] = useState(() => readListState(searchKey));

	const tableInstance = useTable({
		columns,
		data,
		initialState: { 
			pageIndex: 0,
			globalFilter: initialSearch,
			sortBy: [
				{
					id: props.sort_by,
					desc: (props.sort_type === 'desc')
				}
			]
		},
		manualPagination: props.manualPagination,
		manualSortBy: props.manualPagination,
		manualGlobalFilter: props.manualGlobalFilter,
		manualFilters: props.manualFilters,
		pageCount: props.pageCount,
		autoResetPage: !props.manualPagination,
		autoResetSortBy: !props.manualPagination,
		autoResetGlobalFilter: !props.manualGlobalFilter,
		autoResetFilters: !props.manualFilters,
		// Liste il cui server non ordina: niente icone di ordinamento che non fanno nulla
		disableSortBy: props.disableSortBy,
	}, useFilters, useGlobalFilter, useSortBy, usePagination)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		state,
		page,
		gotoPage,
		pageCount,
		pageOptions,
		nextPage,
		previousPage,
		canNextPage,
		canPreviousPage,
		setGlobalFilter,
	} = tableInstance

	const { globalFilter, pageIndex, pageSize, sortBy, filters } = state

	useEffect(() => {
		writeListState(searchKey, globalFilter);
	}, [searchKey, globalFilter]);

	// We need a ref to store the "applied" filters.
	const [appliedFilters, setAppliedFilters] = useState([]);
	const prevFetchArgs = useRef(null);

	// Debounce del testo di ricerca: la chiamata al server parte solo
	// quando l'utente smette di digitare per ~600ms (così c'è il tempo di scrivere)
	const [debouncedGlobalFilter, setDebouncedGlobalFilter] = useState(globalFilter);
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedGlobalFilter(globalFilter);
		}, 600);
		return () => clearTimeout(handler);
	}, [globalFilter]);

	// Filtri esterni alla tabella (es. chip accanto alla ricerca): se cambiano si riparte
	// dalla prima pagina, e la chiamata al server la fa il giro successivo.
	const externalFiltersKey = JSON.stringify(props.externalFilters ?? null);
	const prevExternalFiltersKey = useRef(externalFiltersKey);

	useEffect(() => {
		if (props.manualPagination && props.fetchData) {
			if (prevExternalFiltersKey.current !== externalFiltersKey) {
				prevExternalFiltersKey.current = externalFiltersKey;
				if (pageIndex !== 0) {
					gotoPage(0);
					return;
				}
			}

			let effectiveGlobalFilter = debouncedGlobalFilter;
			if (debouncedGlobalFilter && debouncedGlobalFilter.length < 3) {
				effectiveGlobalFilter = undefined;
			}

			const currentFilters = props.manualFilters ? appliedFilters : filters;

			const fetchArgs = { 
				pageIndex, 
				pageSize, 
				globalFilter: effectiveGlobalFilter, 
				sortBy, 
				filters: currentFilters,
				externalFilters: props.externalFilters,
			};

			// console.log('TabellaListaServer useEffect', { prev: prevFetchArgs.current, current: fetchArgs });

			if (JSON.stringify(prevFetchArgs.current) !== JSON.stringify(fetchArgs)) {
				// console.log('Fetching data...');
				props.fetchData(fetchArgs);
				prevFetchArgs.current = fetchArgs;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageIndex, pageSize, debouncedGlobalFilter, sortBy, props.manualPagination, appliedFilters, filters, props.manualFilters, externalFiltersKey]);

	const [show, setShow] = useState(false);
	const [modalStyle, setModalStyle] = useState({
		position: 'fixed',
		margin: 0,
		top: 0,
		right: '-100%',
		height: '100%',
		transition: 'right 0.5s ease-in-out',
		visibility: 'hidden' // Inizialmente nascondi la modale
	});

	const handleShow = () => {
		setShow(true);
	};

	const handleClose = () => {
		setModalStyle(prevStyle => ({
			...prevStyle,
			right: '-100%',
			visibility: 'hidden' // Nascondi la modale
		}));
		setShow(false);
	};

	const handleFilterSubmit = () => {
		setAppliedFilters(state.filters); // This will trigger the useEffect
		if (state.filters.length > 0) {
			setGlobalFilter(undefined);
		}
		handleClose();
	};

	useEffect(() => {
		if (show) {
			setTimeout(() => {
				setModalStyle(prevStyle => ({
					...prevStyle,
					right: 0,
					visibility: 'visible' // Rendi visibile la modale
				}));
			}, 300);
		}
	}, [show]);

	useEffect(() => {
		setColumns(props.data_column);

		if(props.data_json) {
			setDataJson(props.data_json);
			setMessage('Nessun dato presente');
		} else {
			setMessage('Caricamento in corso');
		}
	}, [props]);

	const headerSlot = usePageHeaderActionsSlot(props.pageList && props.sidLayout);


	// Riga apribile (props.openRow): in fondo una colonna stretta con la freccia, solo come segnale
	// che il clic porta alla scheda. Il clic resta della riga intera.
	const conFreccia = !!props.openRow;
	const numColonne = props.data_column.length + (conFreccia ? 1 : 0);
	return (
		<>
			<div className="card">
				{props.sidLayout ? (
				<div className="card-header sid-list-header">
					{props.pageList ? (
						<PageHeaderActions slot={headerSlot}>
							{props.hasAddMore && props.linkAddMore && <Link to={props.linkAddMore} className="btn btn-primary btn-sm btn-add"> <Add variant="Bulk" /><span className='ms-2'>{props.labelAddMore}</span></Link>}
							{props.hasAddMoreAsModal && <Button onClick={props.modalCallBack} className="btn btn-primary btn-sm btn-add" ><Add variant="Bulk" /><span className='ms-2'>{props.labelAddMore || 'Aggiungi'}</span></Button>}
							{props.headerActions}
						</PageHeaderActions>
					) : (
					<div className="sid-list-header__top">
						<h4 className="card-title">{props.cardTitle}</h4>
						<div className="sid-list-header__actions">
							{props.hasAddMore && props.linkAddMore && <Link to={props.linkAddMore} className="btn btn-primary btn-sm btn-add"> <Add variant="Bulk" /><span className='ms-2'>{props.labelAddMore}</span></Link>}
							{props.hasAddMoreAsModal && <Button onClick={props.modalCallBack} className="btn btn-primary btn-sm btn-add" ><Add variant="Bulk" /><span className='ms-2'>{props.labelAddMore || 'Aggiungi'}</span></Button>}
							{props.headerActions}
						</div>
					</div>
					)}
					{(props.hasSearch || props.filterControl) && (
						<div className="sid-list-header__bottom" ref={rigaFiltri}>
							{props.hasSearch && <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} placeholder={props.searchPlaceholder} />}
							{props.filterControl && <div className="sid-list-header__filter">{props.filterControl}</div>}
						</div>
					)}
				</div>
				) : (
				<div className="card-header">
					<div>
						<h4 className="card-title">{props.cardTitle}</h4>
						{props.cardSubtitle}
					</div>
					<div className="ml-auto d-flex align-items-center">
						{props.hasSearch && <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} placeholder={props.searchPlaceholder} />}
						{props.hasFilter && <Button onClick={handleShow} className="btn btn-dark light btn-sm btn-add me-3"><span className='me-2'>Filtri</span><FilterSquare variant="Bulk" /></Button>}
						{props.hasAddMore && props.linkAddMore && <Link to={props.linkAddMore} className="btn btn-primary btn-sm btn-add"> <Add variant="Bulk" /><span className='ms-2'>{props.labelAddMore}</span></Link>}
						{props.hasAddMoreAsModal && <Button onClick={props.modalCallBack} className="btn btn-primary btn-sm btn-add" ><Add variant="Bulk" /><span className='ms-2'>Aggiungi</span></Button>}
						{/* Azioni proprie della pagina: tengono header, filtri e tabella
						    in un'unica card, come nel mockup. */}
						{props.headerActions}
					</div>
				</div>
				)}
				<div className="card-body">
					{props.filterBar}
					<div className="table-responsive" style={{ overflowX: 'auto' }}>
						<table {...getTableProps()} className="table dataTable display">
							<thead>
								{headerGroups.map(headerGroup => (
									<tr {...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map((column, index, array) => (
											<th key={column.id} {...column.getHeaderProps(column.getSortByToggleProps())} className={column.compact ? 'sid-col-compact' : undefined}>
												{column.render('Header')}
												{props.sidLayout ? (column.canSort && (conFreccia ? (column.id !== 'azione' && column.Header !== 'Azione') : index !== array.length - 1) && (
													<span className="ms-2 sid-sort-ico">
														{column.isSorted
															? (column.isSortedDesc ? <i className="fa fa-arrow-down fs-14" /> : <i className="fa fa-arrow-up fs-14" />)
															: <i className="fa fa-sort fs-14" />}
													</span>
												)) : column.isSorted && (conFreccia ? (column.id !== 'azione' && column.Header !== 'Azione') : index !== array.length - 1) && (
													<span className="ml-1">
														{column.isSortedDesc ? <i className="fa fa-arrow-down ms-2 fs-14" /> : <i className="fa fa-arrow-up ms-2 fs-14" />}
													</span>
												)}
											</th>
										))}
										{conFreccia && <th className="sid-col-arrow" aria-hidden="true" />}
									</tr>
								))}
							</thead>
							<tbody {...getTableBodyProps()} className="">
								{page.length === 0 ? (
									<tr>
										<td colSpan={numColonne} style={{ textAlign: 'center' }}>
											{message}
										</td>
									</tr>
								) : (
									page.map((row) => {
										prepareRow(row);
										// Riga intera cliccabile: apre la scheda come "Visualizza" nel menu azioni.
										// I clic su pulsanti, menu, link e campi dentro la riga fanno la loro azione.
										const apribile = props.openRow && (!props.canOpenRow || props.canOpenRow(row.original));
										const apri = (e) => {
											if (e.target.closest('button, a, input, select, textarea, label, .dropdown, .dropdown-menu, [role="menu"]')) return;
											props.openRow(row.original);
										};
										return (
											<tr
												{...row.getRowProps()}
												className={apribile ? 'sid-row-link' : undefined}
												onClick={apribile ? apri : undefined}
												onKeyDown={apribile ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) props.openRow(row.original); } : undefined}
												tabIndex={apribile ? 0 : undefined}
											>
												{row.cells.map((cell) => (
													<td
														{...cell.getCellProps()}
														className={[apribile && cell.column.id === props.openColumn ? 'sid-row-link__name' : '', cell.column.compact ? 'sid-col-compact' : ''].filter(Boolean).join(' ') || undefined}
													> {cell.render('Cell')} </td>
												))}
												{conFreccia && (
													<td className="sid-col-arrow" aria-hidden="true">
														{apribile && <i className="fa fa-chevron-right" />}
													</td>
												)}
											</tr>
										);
									})
								)}
							</tbody>
						</table>

						{props.sidLayout ? (
						<div className="d-flex align-items-center justify-content-between footTab">
							{props.totalItems !== undefined && props.totalItems !== null ? <span className="sid-list-total">{props.totalItems === 1 ? '1 elemento' : `${props.totalItems} elementi`}</span> : <span />}
							{!props.notPagination &&
								<SidPagination
									pageIndex={pageIndex}
									pageCount={pageCount}
									gotoPage={gotoPage}
									previousPage={previousPage}
									nextPage={nextPage}
									canPreviousPage={canPreviousPage}
									canNextPage={canNextPage}
								/>
							}
						</div>
						) : (
						<div className="d-flex align-items-center justify-content-between footTab">
							{!props.notPagination &&
								<>
									{props.totalItems !== undefined ? (
										<span className="text-muted">
											{props.totalItems === 0
												? 'Nessun risultato'
												: `${pageIndex * pageSize + 1}–${Math.min((pageIndex + 1) * pageSize, props.totalItems)} di ${props.totalItems}`}
										</span>
									) : (
										<span>
											Pagina {' '}
											<strong>
												{pageIndex + 1} di {pageOptions.length}
											</strong>{''}
											{props.itemsCount !== undefined && props.itemsCount !== null && (
												<span className="sid-list-total ms-3">
													{props.itemsCount === 1 ? '1 elemento' : `${props.itemsCount} elementi`}
												</span>
											)}
										</span>
									)}
									<div className="text-end mb-3">
										<div className="filter-pagination  mt-3">
											{props.totalItems !== undefined ? (
												// Numeri di pagina cliccabili come nel mockup. Attivi solo dove
												// la pagina passa `totalItems`: le altre liste restano com'erano.
												<>
													<button className=" previous-button" onClick={() => previousPage()} disabled={!canPreviousPage}>{'‹'}</button>
													{Array.from({ length: pageCount }, (_, i) => i).map(i => (
														<button
															key={i}
															className={i === pageIndex ? 'next-button active' : 'next-button'}
															onClick={() => gotoPage(i)}
														>
															{i + 1}
														</button>
													))}
													<button className=" next-button" onClick={() => nextPage()} disabled={!canNextPage}>{'›'}</button>
												</>
											) : (
												<>
													<button className=" previous-button" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>

													<button className="previous-button" onClick={() => previousPage()} disabled={!canPreviousPage}>
														Indietro
													</button>
													<button className="next-button" onClick={() => nextPage()} disabled={!canNextPage}>
														Avanti
													</button>
													<button className=" next-button" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>
												</>
											)}
										</div>
									</div>
								</>
							}
						</div>
						)}
					</div>
				</div>
			</div>
			{show && (
				<Modal show={show} onHide={handleClose}>
					<div className="modal-dialog" style={modalStyle}>
						<div className="modal-content" style={{ height: '100%', border: 'none', borderRadius: 0 }}>
							<Modal.Header closeButton>
								<Modal.Title>Filtri</Modal.Title>
							</Modal.Header>
							<Modal.Body style={{ overflowY: 'auto' }}>
								{headerGroups.map(headerGroup => (
									<div {...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map(column => (
											column.canFilter && (
												<span key={column.id} {...column.getHeaderProps()}>
													{column.render('Header')}
													{column.canFilter && column.render('Filter')}
												</span>
											)
										))}
									</div>
								))}
							</Modal.Body>
							<Modal.Footer>
								<Button variant="secondary" onClick={handleClose}>
									Chiudi
								</Button>
								{props.manualFilters && (
									<Button variant="primary" onClick={handleFilterSubmit}>
										Filtra
									</Button>
								)}
							</Modal.Footer>
						</div>
					</div>
				</Modal>
			)}

			{props.modalContent && props.modalContent}
		</>
	)

}
export default TabellaListaServer;